import { EM_DASH } from "constants/index";

import { useMemo } from "react";

import { Button, portexColor } from "@portex-pro/ui-components";
import { QuoteUnionByMode } from "api/rest/quote-requests";
import classNames from "classnames";
import OverflowMenuView from "components/OverflowMenuView";
import Text from "components/Text";
import withAsync from "components/withAsync";
import PortexFeaturedPartnerChip from "features/featured-brokers/PortexFeaturedPartnerChip";
import { useSplitQuote } from "features/quote-request";
import useFeaturedBrokers from "hooks/useFeaturedBrokers";
import orderBy from "lodash/orderBy";
import partition from "lodash/partition";
import { DateTime } from "luxon";
import ButtonBookNow from "pages/shipper/components/ButtonBookNow";
import PendingQuote from "pages/shipper/pages/quote-request-details/components/PendingQuote";
import useFeaturedBrokerStyles from "pages/shipper/pages/quote-request-details/hooks/useFeaturedBrokerStyles";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import { formatUSD } from "utils/formatCurrency";
import getTransitTimeString from "utils/getTransitTimeString";

import useLoadQuoteRequest from "../../hooks/quotes/useLoadQuoteRequest";
import { useRequestManagementSliceSelector } from "../../store/requestManagementStore";

const QuoteListContainer = withAsync({
  useHook: () => useLoadQuoteRequest(),
  Component: ({ loadedData: { quoteRequest } }) => {
    const history = useHistory();
    const match = useRouteMatch();
    const sortBy = useRequestManagementSliceSelector((state) => state.quoteIndexSlice.sortBy);
    const { t } = useTranslation(["common", "shipper", "shipments"]);
    const { isFeaturedBroker } = useFeaturedBrokers();
    const featureBrokerStyles = useFeaturedBrokerStyles();
    const { SplitQuoteDialogView, shouldDisplaySplitQuote, setSplitQuoteId } = useSplitQuote(quoteRequest);

    const cheapestQuoteAmount = useMemo(
      () => quoteRequest.quotes.reduce((amount, quote) => Math.min(quote.total_amount, amount), Infinity),
      [quoteRequest]
    );
    const totalDistance = useMemo(
      () => quoteRequest.stops.reduce((acc, stop) => acc + (stop.distance_from_prior_stop || 0), 0),
      [quoteRequest]
    );

    const unsubmittedPartners = useMemo(() => {
      const submittedPartnerEmails = quoteRequest.quotes.map((quote) => quote.submitter_email);
      return quoteRequest.partners.filter((partner) => !submittedPartnerEmails.includes(partner.email));
    }, [quoteRequest]);

    const quotes = useMemo(() => {
      const quotesForMode = quoteRequest.quotes.filter((quote) => quote.mode === quoteRequest.mode);
      // eslint-disable-next-line prefer-const
      let [featuredBrokers, sortedQuotes] = partition(quotesForMode, (quote) =>
        isFeaturedBroker(quote.submitter_email)
      );

      if (sortBy === "CHEAPEST") {
        sortedQuotes = orderBy(sortedQuotes, "total_amount", "asc");
      } else if (sortBy === "FASTEST") {
        sortedQuotes = orderBy(sortedQuotes, "min_transit_time", "asc");
      } else {
        sortedQuotes = orderBy(
          sortedQuotes,
          (quote) => {
            // This typing is necessary to fool the compiler into believing this is a drayage quote
            const quoteValidUntil = (quote as QuoteUnionByMode & { mode: "DRAYAGE" }).valid_until;

            if (!quoteValidUntil) {
              return Infinity;
            }

            return DateTime.fromISO(quoteValidUntil).toMillis();
          },
          "desc"
        );
      }

      return featuredBrokers.concat(sortedQuotes);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteRequest]);

    if (quoteRequest.mode !== "DRAYAGE" && quoteRequest.mode !== "FTL") {
      return null;
    }

    return (
      <>
        <div className="w-full h-full space-y-2">
          {quotes.map((quote) => {
            if (quote.mode !== "DRAYAGE" && quote.mode !== "FTL") {
              return null;
            }
            const isFeatured = isFeaturedBroker(quote.submitter_email);

            return (
              <div
                className={classNames({
                  "border rounded p-5 flex flex-row justify-between relative": true,
                  " bg-white": !isFeatured,
                  "border-2 border-green-500": quote.total_amount === cheapestQuoteAmount && !isFeatured,
                  "border-[3px] border-gold-500 bg-gold-100": isFeatured,
                })}
              >
                {isFeatured && <PortexFeaturedPartnerChip className="absolute left-9 top-[-0.625rem]" />}
                <div className="flex flex-col max-w-[200px] w-[200px]">
                  <Text
                    size="large"
                    weight="bold"
                    typographyProps={{
                      style: {
                        textOverflow: "ellipsis",
                        wordWrap: "break-word",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      },
                    }}
                  >
                    {quote.company_name}
                  </Text>

                  {cheapestQuoteAmount === quote.total_amount && (
                    <Text
                      size="x-small"
                      weight="bold"
                      typographyProps={{
                        style: { color: portexColor.green500, textTransform: "uppercase", paddingTop: "6px" },
                      }}
                    >
                      {t("common:cheapest")}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col">
                  <Text size="small">{t("common:validUntil")}</Text>
                  <Text size="medium" weight="bold">
                    {quote.valid_until
                      ? DateTime.fromISO(quote.valid_until).toLocaleString(DateTime.DATE_MED)
                      : EM_DASH}
                  </Text>
                </div>

                {quote.mode === "DRAYAGE" && (
                  <div className="flex flex-col">
                    <Text size="small">{t("shipments:shipmentQuotes_estimatedTransitTime_label")}</Text>
                    <Text size="medium" weight="bold">
                      {getTransitTimeString(quote.min_transit_time, quote.max_transit_time)}
                    </Text>
                  </div>
                )}

                {quote.mode === "FTL" && (
                  <div className="flex flex-col">
                    <Text size="small">{t("common:estPerMile")}</Text>
                    <Text size="medium" weight="bold">
                      {totalDistance === 0 ? t("common:na") : formatUSD(quote.rate_per_load / totalDistance)}
                    </Text>
                  </div>
                )}

                {quote.mode === "DRAYAGE" && (
                  <div className="flex flex-col">
                    <Text size="small">{t("common:totalAmount")}</Text>
                    <Text
                      size="medium"
                      weight="bold"
                      typographyProps={{
                        style: { color: quote.total_amount === cheapestQuoteAmount ? portexColor.green500 : undefined },
                      }}
                    >
                      {formatUSD(quote.total_amount)}
                    </Text>
                  </div>
                )}

                {quote.mode === "FTL" && (
                  <div className="flex flex-col">
                    <Text size="small">{t("common:perLoad")}</Text>
                    <Text
                      size="medium"
                      weight="bold"
                      typographyProps={{
                        style: {
                          color:
                            quote.total_amount === cheapestQuoteAmount
                              ? portexColor.green500
                              : isFeatured
                              ? portexColor.gold400
                              : undefined,
                        },
                      }}
                    >
                      {formatUSD(quote.rate_per_load)}
                    </Text>
                  </div>
                )}

                <div className="flex flex-row space-x-2 min-w-[310px]">
                  <Button
                    variant="outlined"
                    color="primary"
                    style={{ whiteSpace: "nowrap", width: "100%", padding: "0.5rem 2rem" }}
                    className={isFeatured ? featureBrokerStyles.featuredSecondaryButton : undefined}
                    onClick={() => history.push(`${match.url}/quote/${quote._quote_id}`)}
                  >
                    {t("shipments:shipmentQuotes_viewQuote_button")}
                  </Button>
                  {quoteRequest.status === "BOOKED" || quotes.some((quote) => quote.status === "BOOKED") ? (
                    <>
                      {quote.status === "BOOKED" ? (
                        <ButtonBookNow bookNowVariant="booked" style={{ pointerEvents: "none" }} />
                      ) : (
                        <ButtonBookNow bookNowVariant="booked-by-other" />
                      )}
                    </>
                  ) : (
                    <ButtonBookNow
                      bookNowVariant="book"
                      // @ts-expect-error: This is OK
                      component={Link}
                      className={isFeatured ? featureBrokerStyles.featuredPrimaryButton : undefined}
                      style={{ height: "100%" }}
                      to={`${match.url}/quote/${quote._quote_id}`}
                    />
                  )}

                  {shouldDisplaySplitQuote && (
                    <div
                      className="self-center"
                      style={{
                        // tailwind's 'flex-grow-0' does not have higher specificity than some MUI styles here. So we add inline style here
                        flexGrow: 0,
                      }}
                    >
                      <OverflowMenuView
                        items={[
                          {
                            label: t("shipper:splitQuoteDialog.separateToMultiLoad"),
                            onClick: () => setSplitQuoteId(quote._quote_id),
                          },
                        ]}
                        buttonStyleProps={{
                          color: isFeatured ? portexColor.gold500 : portexColor.blue500,
                          backgroundColor: "transparent",
                          border: "none",
                          minWidth: 0,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {SplitQuoteDialogView}
          {unsubmittedPartners.length > 0 &&
            unsubmittedPartners.map(({ company_name }) => <PendingQuote companyName={company_name} />)}
        </div>
      </>
    );
  },
});

export default QuoteListContainer;
